import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Container,
  Card,
  Form,
  Button,
  Spinner,
  Alert,
  Row,
  Col,
} from "react-bootstrap";
import { FaPlusCircle } from "react-icons/fa";
import api from "../api";
import { AuthContext } from "../context/AuthContext";

const defaultForm = (user) => ({
  name: "",
  description: "",
  category: "",
  price: "",
  type: "rent",
  state: user?.state || "",
  city: user?.city || "",
  contactEmail: user?.email || "",
  contactPhone: user?.phone || "",
  images: "",
});

export default function PostListing() {
  const { user } = useContext(AuthContext);
  const [formData, setFormData] = useState(defaultForm(user));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [imageFiles, setImageFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      state: user?.state || "",
      city: user?.city || "",
      contactEmail: user?.email || "",
      contactPhone: user?.phone || "",
    }));
  }, [user]);

  useEffect(() => {
    const nextPreviews = imageFiles.map((file) => ({
      name: file.name,
      url: URL.createObjectURL(file),
    }));

    setImagePreviews((prev) => {
      prev.forEach((preview) => URL.revokeObjectURL(preview.url));
      return nextPreviews;
    });

    return () => {
      nextPreviews.forEach((preview) => URL.revokeObjectURL(preview.url));
    };
  }, [imageFiles]);

  const updateField = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 6) {
      setError("You can upload up to 6 images per listing.");
      setImageFiles(files.slice(0, 6));
    } else {
      setError((prev) =>
        prev === "You can upload up to 6 images per listing." ? "" : prev
      );
      setImageFiles(files);
    }
  };

  const clearSelectedFiles = () => {
    setImageFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");

    if (!user?.email) {
      setError("You need to be logged in to publish a listing.");
      return;
    }

    if (!formData.name.trim() || !formData.description.trim()) {
      setError("Name and description are required.");
      return;
    }

    setSubmitting(true);
    try {
      const manualImages = formData.images
        .split(/[\n,]/)
        .map((url) => url.trim())
        .filter(Boolean);

      let uploadedImagePaths = [];
      if (imageFiles.length > 0) {
        const uploadData = new FormData();
        imageFiles.forEach((file) => uploadData.append("images", file));
        try {
          const uploadRes = await api.post("/items/upload-images", uploadData, {
            headers: { "Content-Type": "multipart/form-data" },
          });

          if (uploadRes.data?.success) {
            uploadedImagePaths = uploadRes.data.images || [];
          } else {
            throw new Error(uploadRes.data?.message || "Image upload failed");
          }
        } catch (uploadErr) {
          console.error("Image upload error:", uploadErr);
          throw new Error(
            uploadErr.response?.data?.message ||
              uploadErr.message ||
              "Failed to upload item images."
          );
        }
      }

      const imageList = [...manualImages, ...uploadedImagePaths];

      const payload = {
        ownerEmail: user.email,
        name: formData.name.trim(),
        description: formData.description.trim(),
        category: formData.category.trim() || undefined,
        price: formData.price !== "" ? Number(formData.price) : undefined,
        type: formData.type,
        state: formData.state.trim() || undefined,
        city: formData.city.trim() || undefined,
        contactEmail: formData.contactEmail.trim() || undefined,
        contactPhone: formData.contactPhone.trim() || undefined,
        images: imageList.length > 0 ? imageList : undefined,
      };

      const res = await api.post("/items", payload);
      if (res.data?.success) {
        setSuccess("Listing published successfully.");
        setFormData(defaultForm(user));
        clearSelectedFiles();
      } else {
        setError(res.data?.message || "Failed to publish the listing.");
      }
    } catch (err) {
      console.error("Listing publish error:", err);
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to publish the listing."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container className="py-4">
      <Card
        className="shadow border-0 rounded-4 mx-auto"
        style={{ maxWidth: 720 }}
      >
        <Card.Body className="p-4">
          <div className="d-flex align-items-center mb-3">
            <FaPlusCircle className="text-primary me-2" size={22} />
            <h4 className="mb-0">Post a Marketplace Item</h4>
          </div>
          <p className="text-muted mb-4">
            Share items for rent or sale. Provide accurate contact information
            so interested users can reach you quickly.
          </p>
          {error && <Alert variant="danger">{error}</Alert>}
          {success && <Alert variant="success">{success}</Alert>}
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Item name</Form.Label>
              <Form.Control
                name="name"
                value={formData.name}
                onChange={updateField}
                placeholder="e.g. 2 BHK Apartment"
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={formData.description}
                onChange={updateField}
                placeholder="Highlight the key details buyers or tenants should know"
                required
              />
            </Form.Group>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Category</Form.Label>
                  <Form.Control
                    name="category"
                    value={formData.category}
                    onChange={updateField}
                    placeholder="Appliances, Furniture, Electronics..."
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Listing type</Form.Label>
                  <Form.Select
                    name="type"
                    value={formData.type}
                    onChange={updateField}
                  >
                    <option value="rent">For Rent</option>
                    <option value="sale">For Sale</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3 mt-3">
              <Form.Label>Price (₹)</Form.Label>
              <Form.Control
                type="number"
                min="0"
                step="1"
                name="price"
                value={formData.price}
                onChange={updateField}
                placeholder="Enter the price or monthly rent"
              />
            </Form.Group>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>City</Form.Label>
                  <Form.Control
                    name="city"
                    value={formData.city}
                    onChange={updateField}
                    placeholder="City"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>State</Form.Label>
                  <Form.Control
                    name="state"
                    value={formData.state}
                    onChange={updateField}
                    placeholder="State"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="g-3 mt-1">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Contact email</Form.Label>
                  <Form.Control
                    type="email"
                    name="contactEmail"
                    value={formData.contactEmail}
                    onChange={updateField}
                    placeholder="contact@example.com"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Contact phone</Form.Label>
                  <Form.Control
                    name="contactPhone"
                    value={formData.contactPhone}
                    onChange={updateField}
                    placeholder="Phone number"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-4 mt-3">
              <Form.Label>Upload product images</Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileChange}
                ref={fileInputRef}
              />
              <Form.Text className="text-muted">
                Add up to six images (PNG, JPG, or WebP, max 5MB each).
              </Form.Text>
              {imagePreviews.length > 0 && (
                <Row className="g-3 mt-2">
                  {imagePreviews.map((preview) => (
                    <Col xs={6} sm={4} key={preview.url}>
                      <div className="border rounded-3 p-2 h-100 d-flex flex-column align-items-center">
                        <img
                          src={preview.url}
                          alt={preview.name}
                          className="img-fluid rounded mb-2"
                          style={{
                            width: "100%",
                            maxHeight: 140,
                            objectFit: "cover",
                          }}
                        />
                        <div className="text-truncate small w-100 text-center">
                          {preview.name}
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              )}
              {imagePreviews.length > 0 && (
                <div className="mt-2">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={clearSelectedFiles}
                  >
                    Clear selected images
                  </Button>
                </div>
              )}
            </Form.Group>
            <Form.Group className="mb-4 mt-3">
              <Form.Label>Image URLs (optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="images"
                value={formData.images}
                onChange={updateField}
                placeholder={
                  "Paste additional image links, separated by commas or new lines"
                }
              />
            </Form.Group>
            <div className="d-grid">
              <Button type="submit" disabled={submitting}>
                {submitting ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  "Publish listing"
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
}
